<template>
  <Form
    @submit="onSubmit"
    :class="classnames"
    v-slot="{ errors, isSubmitting }"
    :validation-schema="getValidationSchema"
  >
    <div class="summary_title_items_wrapper">
      <div class="flex w-full items-center gap-x-8 max-md:justify-between">
        <span class="summary_title">{{ props.title }}</span>
        <template v-if="props.disabled === 'false'">
          <button
            type="button"
            v-if="!summaryDataObject.isEditable"
            class="summary_edit_button border-dark_main text-dark_main dark:border-main dark:text-main hover:border-main dark:hover:text-background px-3 py-1.5 md:hidden"
            @click="openEdit()"
          >
            Edit {{ route.name == 'success' ? 'Profile' : 'ride' }}
          </button>
          <button
            :disabled="isSubmitting"
            type="submit"
            v-if="summaryDataObject.isEditable"
            class="summary_edit_button save min-w-max border-none px-3 py-1.5 md:hidden"
          >
            Confirm
          </button>
        </template>
      </div>
      <div class="flex w-full flex-row gap-x-[30px] max-md:flex-wrap md:gap-x-[60px]">
        <div
          class="!flex w-full flex-wrap justify-between gap-5 md:gap-x-6 md:gap-y-4"
          :class="{ 'lg:!grid lg:grid-cols-3': props.prepare === 'contactData' }"
        >
          <!-- Contact fields component -->
          <ContactFields
            :is-editable="summaryDataObject.isEditable"
            :errors="errors"
            :check-field="checkField"
            ref="contactFieldsRef"
          />

          <!-- Location fields component -->
          <LocationFields
            :is-editable="summaryDataObject.isEditable"
            :errors="errors"
            :check-field="checkField"
          />

          <!-- Ride details fields component -->
          <RideDetailsFields
            :is-editable="summaryDataObject.isEditable"
            :errors="errors"
            :check-field="checkField"
          />

          <!-- Car selection field component -->
          <CarSelectionField
            :check-field="checkField"
            :is-modal-open="summaryDataObject.isModal"
            @open-modal="openModal"
            @close-modal="closeModal"
          />

          <template v-if="props.disabled === 'false' && !paymentVisited">
            <button
              type="button"
              v-if="!summaryDataObject.isEditable"
              :class="{
                '!w-[260px]': props.customStyle == 'true',
                'w-full': props.customStyle == 'false',
                'lg:col-start-3 lg:self-end': props.prepare === 'contactData'
              }"
              class="summary_edit_button border-dark_main text-dark_main dark:border-main dark:text-main hover:border-main dark:hover:text-background ml-auto mr-0 h-16 w-full min-w-max px-3 py-1.5 max-md:hidden"
              @click="openEdit"
            >
              Edit {{ route.name == 'success' ? 'Profile' : 'ride' }}
            </button>
            <button
              :disabled="isSubmitting"
              type="submit"
              v-if="summaryDataObject.isEditable"
              :class="{
                '!w-[260px]': props.customStyle == 'true',
                'w-full': props.customStyle == 'false',
                'lg:col-start-3 lg:self-end': props.prepare === 'contactData'
              }"
              class="summary_edit_button save ml-auto mr-0 h-16 w-full min-w-max border-none px-3 py-1.5 max-md:hidden"
            >
              Confirm
            </button>
          </template>
        </div>
      </div>
    </div>
    <slot></slot>
  </Form>
  <swipe-modal
    v-model="summaryDataObject.isModal"
    v-if="(isMobile && !mailCar) || !mailCar"
    contents-height="60vh"
    border-top-radius="16px"
    :contents-color="mode === 'dark' ? '#2B2D32' : '#FFFFFF'"
    tip-color="#CDCFD0"
    @close="closeModal()"
  >
    <car-popup-content />
  </swipe-modal>
</template>

<script setup>
import * as Sentry from '@sentry/browser'
import { collect } from 'collect.js'
import { computed, inject, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Form } from 'vee-validate'
import { storeToRefs } from 'pinia'
import { useCarsStore, useContactsStore, useMainStore, useOrderStore } from '@/stores'
import swipeModal from '@takuma-ru/vue-swipe-modal'
import { useMobile } from '@/compose/ismobile'
import CarPopupContent from '@/components/features/car/CarPopupContent.vue'
import ContactFields from './components/ContactFields.vue'
import LocationFields from './components/LocationFields.vue'
import RideDetailsFields from './components/RideDetailsFields.vue'
import CarSelectionField from './components/CarSelectionField.vue'
import { useValidationSchema } from './composables/useValidationSchema'
import { useFieldVisibility } from './composables/useFieldVisibility'

const mainStore = useMainStore()
const orderStore = useOrderStore()
const contactsStore = useContactsStore()
const carsStore = useCarsStore()

const { orderData } = storeToRefs(orderStore)
const { orderId, mailCar } = storeToRefs(orderStore)
const { mode, isRequesting } = storeToRefs(mainStore)

const { isMobile } = useMobile()
const axios = inject('axios')
const route = useRoute()
const contactFieldsRef = ref(null)

const props = defineProps(['title', 'customStyle', 'disabled', 'classnames', 'prepare'])
const prepareRef = computed(() => props.prepare)

const { checkField } = useFieldVisibility(prepareRef)
const { validationSchema } = useValidationSchema()

const getValidationSchema = computed(() => {
  const fields = checkField
    ? Object.keys(validationSchema).filter((field) => checkField(field))
    : []
  const schema = collect(validationSchema).only(fields).all()
  return schema
})

const paymentVisited = ref(false)

let summaryData = {
  isModal: false,
  isEditable: false,
  dateInputStyles: {
    pointerEvents: 'none'
  }
}
let summaryDataObject = reactive(summaryData)

const pudoChanged = ref(false)

const changeButtonColor = (mode) => {
  if (mode === 'light') {
    summaryDataObject.dateInputStyles = {
      pointerEvents: 'all',
      color: 'rgb(45 132 35 / 1)',
      borderBottom: '1px solid rgb(45 132 35 / 1)'
    }
  } else {
    summaryDataObject.dateInputStyles = {
      pointerEvents: 'all',
      color: 'rgb(204 242 200 / 1)',
      borderBottom: '1px solid rgb(204 242 200 / 1)'
    }
  }
}

const openEdit = () => {
  summaryDataObject.isEditable = true
  changeButtonColor(mode.value)
}

watch(mode, (newMode) => {
  changeButtonColor(newMode)
})

const onSubmit = async (values) => {
  const phoneNationalNumber = contactFieldsRef.value?.phoneNationalNumber?.value || ''

  values.code = orderData.value.code
  values.phone = phoneNationalNumber || orderData.value.phone || ''
  values.country_prefix = orderData.value.country_prefix
  values.date_start = orderData.value.date_start
  values.contact_id = orderData.value.contact_id
  values.type_of_service = orderData.value.type_of_service

  if (orderData.value.type_of_service === 'hourlyAsDirected' && route.name != 'success') {
    values.distance = values.hours * 20
  }

  orderStore.update(values)
  contactsStore.add(orderData.value)

  if (
    orderId.value !== 'null' &&
    orderId.value &&
    (route.name != 'success' || orderData.value.consulting)
  ) {
    isRequesting.value = true
    await axios
      .post('/orders/update/orderSummary/' + orderId.value, values)
      .then(function (response) {
        if (
          response.data.data.cars !== undefined &&
          response.data.data.cars !== null &&
          response.data.data?.cars.length > 0
        ) {
          let new_cars = response.data.data.cars
          carsStore.update(new_cars)
        }
        if (response.data.data.order !== undefined && response.data.data.order !== null) {
          let data_order = response.data.data.order
          orderStore.updateOrder(data_order)
          orderStore.updateOrderId(data_order.id)
          orderStore.update({ distance: data_order.distance })

          if (pudoChanged.value) {
            axios.post('/fields/search/' + orderId.value, {}).then(() => {})
          }
          isRequesting.value = false
        }
      })
      .catch(function (error) {
        isRequesting.value = false
        Sentry.captureException(error)
      })
  }

  if (
    (orderData.value.deal_id || orderData.value.type_of_service == 'toursRoadshows') &&
    route.name == 'success'
  ) {
    if (orderData.value.type_of_service == 'toursRoadshows') {
      values['lead_id'] = orderData.value.lead_id
    }
    if (orderData.value.deal_id) {
      values['deal_id'] = orderData.value.deal_id
    }
    isRequesting.value = true

    values.phone = `${values.phone === null ? '' : values.phone}`

    await axios
      .post('/success/editContact', values)
      .then(() => {
        isRequesting.value = false
      })
      .catch((error) => {
        isRequesting.value = false
        Sentry.captureException(error)
      })
  }

  summaryDataObject.isEditable = false
  summaryDataObject.dateInputStyles = {
    pointerEvents: 'none',
    color: 'unset',
    borderCollor: 'transparent'
  }
}

const openModal = () => {
  summaryDataObject.isModal = true
  document.querySelector('html').style.touchAction = 'none'
  document.body.style.touchAction = 'none'
  document.querySelector('html').style.overflowY = 'hidden'
  document.body.style.overflowY = 'hidden'
  document.querySelector('html').style.overscrollBehavior = 'none'
  document.body.style.overscrollBehavior = 'none'
}

const closeModal = () => {
  summaryDataObject.isModal = false
  document.querySelector('html').style.touchAction = 'auto'
  document.body.style.touchAction = 'auto'
  document.querySelector('html').style.overflowY = 'auto'
  document.body.style.overflowY = 'auto'
  document.querySelector('html').style.overscrollBehavior = 'auto'
  document.body.style.overscrollBehavior = 'auto'
}
</script>

<style>
.hide_phone {
  display: none;
}
</style>
